import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Container, Typography, Button, CircularProgress, Stack,
} from "@mui/material";
import { CheckCircle, ErrorOutline } from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import { apiClient } from "../services/authService";
import { clearLeafletCart } from "../utils/leafletCart";

const NAVY = "#0A1628";
const BLUE = "#1565C0";
const MUTED = "#6B7280";

type Phase = "checking" | "active" | "pending" | "failed";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 12; // ~30s

const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const subscriptionId = searchParams.get("subscriptionId");
  const [phase, setPhase] = useState<Phase>("checking");

  useEffect(() => {
    if (!subscriptionId) {
      setPhase("pending");
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await apiClient.get<{ data: { status: string } }>(
          `/payments/whish/status/${subscriptionId}`
        );
        const status = res.data.data.status;
        if (cancelled) return;

        if (status === "active" || status === "trial") {
          // Paid — the cart has become the order.
          clearLeafletCart();
          setPhase("active");
          return;
        }
        if (status === "cancelled" || status === "expired") {
          setPhase("failed");
          return;
        }
      } catch {
        // ignore transient errors and keep polling
      }

      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        if (!cancelled) setPhase("pending");
        return;
      }
      if (!cancelled) setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => { cancelled = true; };
  }, [subscriptionId]);

  return (
    <Container maxWidth="sm" sx={{ pt: 12, pb: 6, textAlign: "center" }}>
      <Helmet>
        <title>Payment Status — Dr.Paw</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {phase === "checking" && (
        <Box>
          <CircularProgress size={56} sx={{ color: BLUE, mb: 3 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: NAVY }}>
            Confirming your payment…
          </Typography>
          <Typography sx={{ color: MUTED, mt: 1 }}>
            This usually takes a few seconds.
          </Typography>
        </Box>
      )}

      {phase === "active" && (
        <Box>
          <Box sx={{
            width: 88, height: 88, borderRadius: "50%",
            bgcolor: "#ECFDF5", mx: "auto", mb: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <CheckCircle sx={{ fontSize: 52, color: "#10B981" }} />
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: NAVY, mb: 1 }}>
            Payment Successful!
          </Typography>
          <Typography sx={{ color: MUTED, mb: 4 }}>
            Your subscription is now active. Enjoy your courses!
          </Typography>
          <Button variant="contained" onClick={() => navigate("/membership/classes")}
            sx={{ borderRadius: "50px", px: 4, fontWeight: 700, bgcolor: BLUE, "&:hover": { bgcolor: "#0D47A1" } }}>
            Go to Classes
          </Button>
        </Box>
      )}

      {phase === "pending" && (
        <Box>
          <Box sx={{
            width: 88, height: 88, borderRadius: "50%",
            bgcolor: "#FFF7ED", mx: "auto", mb: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ErrorOutline sx={{ fontSize: 52, color: "#F59E0B" }} />
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: NAVY, mb: 1 }}>
            Still Confirming
          </Typography>
          <Typography sx={{ color: MUTED, mb: 4 }}>
            We're still waiting on confirmation from Whish. This can take a moment —
            check your subscription status shortly.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="outlined" onClick={() => navigate("/")}
              sx={{ borderRadius: "50px", px: 3, fontWeight: 600, borderColor: BLUE, color: BLUE }}>
              Back to Home
            </Button>
            <Button variant="contained" onClick={() => navigate("/membership/classes")}
              sx={{ borderRadius: "50px", px: 3, fontWeight: 700, bgcolor: BLUE, "&:hover": { bgcolor: "#0D47A1" } }}>
              Go to Classes
            </Button>
          </Stack>
        </Box>
      )}

      {phase === "failed" && (
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: NAVY, mb: 1 }}>
            Payment Not Completed
          </Typography>
          <Typography sx={{ color: MUTED, mb: 4 }}>
            It looks like this payment didn't go through. Please try again.
          </Typography>
          <Button variant="contained" onClick={() => navigate("/")}
            sx={{ borderRadius: "50px", px: 4, fontWeight: 700, bgcolor: BLUE, "&:hover": { bgcolor: "#0D47A1" } }}>
            Back to Home
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default SubscriptionSuccess;
